import { useContext } from "react";
import {
  KursusFiltersContext,
  KursusFiltersEventContext,
} from "../../../../context/FiltersContext";
import {
  QueryFilter,
  KursusFilterKeys,
  KursusTypeSetting,
} from "../../../../interfaces/filters";
import { KursusholdFilterInput } from "../../../../interfaces/schema";
import { f } from "../../../../util/f";
import { AccordionFilterItem } from "../../../accordion/Accordion";
import AppliedFilterItem from "../../../filter/AppliedFilterItem";
import { KodeSelect } from "../../../formcontrols/KodeSelect";

// helper
const filterKey: QueryFilter<KursusFilterKeys>["filterKey"] = "kursustype";

export default function KursusTypeFilter() {
  // query filter context
  const filters = useContext(KursusFiltersContext);
  const updateFilters = useContext(KursusFiltersEventContext);

  // find the filter settings
  const settings: KursusTypeSetting = filters.find(
    (f) => f.filterKey === filterKey,
  )?.settings;

  // handlers
  function updateFilterHandler(selectedItem: KodeListe | null) {
    // get active list if any otherwise initialise an empty array
    const activeFilters = settings?.activeFilters || [];

    // add selected item if it does not exist in active list
    if (
      selectedItem &&
      !activeFilters.some((s) => s.kode === selectedItem.nr)
    ) {
      activeFilters.push({
        kode: selectedItem.nr,
        type: selectedItem.navn,
      });
    }

    const filters: KursusholdFilterInput[] = activeFilters.map((set) => {
      return {
        and: [
          {
            kursustype: {
              eq: set.kode,
            },
          },
        ],
      };
    });

    // build the filter string
    const filterString = f<KursusholdFilterInput>()
      .addFilter("or", filters)
      .build();

    const updatedSettings: KursusTypeSetting = {
      activeFilters: activeFilters,
    };

    const queryFilter: QueryFilter<KursusFilterKeys, KursusTypeSetting> = {
      filter: filterString,
      filterKey: filterKey,
      settings: updatedSettings,
      parentFilter: "and",
    };

    updateFilters({
      event: "update",
      queryFilter: queryFilter,
    });
  }

  function removeFilterHandler(
    kode: KursusTypeSetting["activeFilters"][number]["kode"],
  ) {
    const updatedFilters =
      settings?.activeFilters?.filter((set) => {
        return kode !== set.kode;
      }) || [];

    const filters: KursusholdFilterInput[] = updatedFilters.map((set) => {
      return {
        and: [
          {
            kursustype: {
              eq: set.kode,
            },
          },
        ],
      };
    });

    // build the filter string
    const filterString = f<KursusholdFilterInput>()
      .addFilter("or", filters)
      .build();

    const updatedSettings: KursusTypeSetting = {
      activeFilters: updatedFilters,
    };

    const update = updatedSettings?.activeFilters?.length > 0;

    if (update) {
      updateFilters({
        event: "update",
        queryFilter: {
          filterKey: filterKey,
          filter: filterString,
          settings: updatedSettings,
        },
      });
    } else {
      updateFilters({
        event: "delete",
        queryFilter: {
          filterKey: filterKey,
        },
      });
    }
  }

  return (
    <AccordionFilterItem
      title="Kursustype"
      value="kursustype"
      isActive={filters.some((f) => f.filterKey === filterKey)}
    >
      {settings && (
        <div className="c-applied-filter-group">
          {settings.activeFilters.map((filter) => (
            <AppliedFilterItem
              key={filter.kode}
              label={`${filter.type} (${filter.kode})`}
              removeFilterHandler={() => removeFilterHandler(filter.kode)}
            />
          ))}
        </div>
      )}

      <KodeSelect
        placeholder="Vælg kursustype"
        onChange={(item) => {
          updateFilterHandler(item);
        }}
        value={undefined}
        kodeListeType="Kursustype"
      />
    </AccordionFilterItem>
  );
}
